import { useEffect, useState } from "react";
import { Loader2, ChevronLeft, ChevronRight, ShieldCheck, Shield } from "lucide-react";
import { apiClient } from "../../lib/api";

import ValidacionStatsStrip from "./components/ValidacionStatsStrip";
import ValidacionFilterToolbar from "./components/ValidacionFilterToolbar";
import ValidacionGrid from "./components/ValidacionGrid";
import ValidacionDrawer from "./components/ValidacionDrawer";

// ----------------------------------------------------------------------
// Interfaces del Backend
// ----------------------------------------------------------------------
interface BackendDoc {
    userVerifyDocId: number;
    userVerifyDocTypeCode: string;
    userVerifyDocTypeName: string;
    docLink: string;
    description: string;
    status: string; // Ej: "APR", "REJ", "PEN"
}

interface BackendVerificationItem {
    userVerifyId: number;
    userId: number;
    userName: string;
    userFullName: string;
    estadoCodigo: string; // "PEN", "APR", "REJ"
    fechaSolicitud: string;
    numero: string;
    documents: BackendDoc[];
}

// ----------------------------------------------------------------------
// Interfaces de la UI (Visuales)
// ----------------------------------------------------------------------
export type DocStatus = 'ok' | 'pending' | 'missing';
export type VerifStatus = 'pending' | 'review' | 'approved' | 'rejected';

export interface DocRequirement {
    id?: number;
    name: string;
    emoji: string;
    status: DocStatus;
    docLink?: string;
}

export interface VerificationRequest {
    id: string;
    backendUserId: number;
    name: string;
    role: 'huesped' | 'anfitrion';
    date: string;
    avatarGradient: string;
    initials: string;
    status: VerifStatus;
    riskScore: number;
    docs: DocRequirement[];
}

const pageSize = 12;

const ValidacionIdentidadPageNew = () => {
    // --- ESTADOS DE UI (SIN MOCKS) ---
    const [requests, setRequests] = useState<VerificationRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState("pending"); // Empezamos en pendientes

    // --- ESTADOS DE PAGINACIÓN ---
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<any>(null);

    // --- ESTADOS DEL DRAWER ---
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedReq, setSelectedReq] = useState<VerificationRequest | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: "success" | "danger" | "info" | "warning" } | null>(null);

    // ----------------------------------------------------------------------
    // LÓGICA INTELIGENTE PARA ESTRUCTURAR LOS DOCUMENTOS
    // ----------------------------------------------------------------------
    const buildFixedDocs = (apiDocs: BackendDoc[], parentStatusCode: string): DocRequirement[] => {
        const baseDocs: DocRequirement[] = [
            { name: "DNI Frente", emoji: "🪪", status: "missing" },
            { name: "DNI Dorso", emoji: "🪪", status: "missing" }
        ];

        const isApproved = parentStatusCode === 'APR';
        const isRejected = parentStatusCode === 'REJ';

        if (!apiDocs || apiDocs.length === 0) {
            if (isApproved) return baseDocs.map(d => ({ ...d, status: 'ok' }));
            if (isRejected) return baseDocs.map(d => ({ ...d, status: 'missing' }));
            return baseDocs;
        }

        apiDocs.forEach(d => {
            // Unimos Name, Code y Description para que sea imposible que se escape
            const searchString = `${d.userVerifyDocTypeName} ${d.userVerifyDocTypeCode} ${d.description}`.toLowerCase();
            let index = -1;

            // Búsqueda súper robusta
            if (searchString.includes('frente') || searchString.includes('anverso') || searchString.includes('front') || searchString.includes('frontal')) {
                index = 0;
            } 
            else if (searchString.includes('dorso') || searchString.includes('reverso') || searchString.includes('back') || searchString.includes('atras') || searchString.includes('trasero')) {
                index = 1;
            } 
            else {
                // Fallback: Busca el primer hueco que aún no tenga un link asignado (evita sobrescribir)
                index = baseDocs.findIndex(bd => !bd.docLink);
            }

            if (index !== -1 && index < baseDocs.length) {
                let docStatus: DocStatus = 'pending';
                if (isApproved || d.status === 'APR') docStatus = 'ok';
                else if (isRejected || d.status === 'REJ') docStatus = 'missing';
                else docStatus = 'pending';

                baseDocs[index] = {
                    id: d.userVerifyDocId,
                    name: d.userVerifyDocTypeName || baseDocs[index].name,
                    emoji: baseDocs[index].emoji,
                    status: docStatus,
                    docLink: d.docLink || undefined
                };
            }
        });

        // Forzamos que nada quede "pendiente" si el trámite ya está cerrado
        baseDocs.forEach(doc => {
            if (isApproved) doc.status = 'ok';
            if (isRejected && doc.status === 'pending') doc.status = 'missing';
        });

        return baseDocs;
    };

    const mapBackendStatus = (code: string): VerifStatus => {
        switch (code) {
            case 'APR': return 'approved';
            case 'REJ': return 'rejected';
            default: return 'pending';
        }
    };

    const mapBackendToUI = (item: BackendVerificationItem): VerificationRequest => {
        const names = item.userFullName ? item.userFullName.split(' ') : ['U'];
        const initials = names.map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase();

        const gradients = [
            "from-blue-500 to-blue-700", "from-purple-500 to-purple-700",
            "from-pink-500 to-pink-700", "from-teal-500 to-teal-700",
            "from-indigo-500 to-indigo-700", "from-amber-500 to-amber-700"
        ];
        const gradient = gradients[(item.userVerifyId || 0) % gradients.length];

        const mappedDocs = buildFixedDocs(item.documents, item.estadoCodigo);

        // Calculamos un riesgo dinámico basado en cuántos documentos tiene aprobados (ahora son 2 docs, por ende * 50)
        const approvedCount = mappedDocs.filter(d => d.status === 'ok').length;
        const calculatedRisk = item.estadoCodigo === 'REJ' ? 90 : item.estadoCodigo === 'APR' ? 10 : (100 - (approvedCount * 50));

        return {
            id: String(item.userVerifyId),
            backendUserId: item.userId,
            name: item.userFullName || "Usuario",
            role: 'huesped',
            date: item.fechaSolicitud ? new Date(item.fechaSolicitud).toLocaleDateString('es-ES') : '---',
            avatarGradient: gradient,
            initials: initials,
            status: mapBackendStatus(item.estadoCodigo),
            riskScore: calculatedRisk,
            docs: mappedDocs
        };
    };

    // ----------------------------------------------------------------------
    // EFECTOS Y LLAMADAS A LA API
    // ----------------------------------------------------------------------
    const fetchVerifications = async () => {
        setLoading(true);
        try {
            let backendStatus = '';
            if (activeFilter === 'pending') backendStatus = 'PEN';
            else if (activeFilter === 'approved') backendStatus = 'APR';
            else if (activeFilter === 'rejected') backendStatus = 'REJ';
            // Si activeFilter === 'todos', enviamos backendStatus vacío.

            const endpoint = `GetPendingVerifications?pageNumber=${currentPage}&pageSize=${pageSize}&statusFilter=${backendStatus}`;
            const response = await apiClient.get<any>(endpoint);
            const result = response?.data || response || [];

            setRequests(result.map(mapBackendToUI));
            setPagination(response.pagination);
        } catch (error) {
            console.error("Error al cargar validaciones:", error);
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVerifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter, currentPage]);

    // Cargar Detalle de Documentos (Solo al abrir el Drawer)
    useEffect(() => {
        const fetchUserDocs = async () => {
            if (!selectedReq || !isDrawerOpen) return;

            setLoadingDetail(true);
            try {
                const res = await apiClient.get<any>(`GetUserVerificationWithDocs/${selectedReq.backendUserId}`);
                const docs = res?.data?.documents || res?.documents || [];

                // Pasamos los documentos reales por nuestro estructurador
                const parentStatusBackend = selectedReq.status === 'approved' ? 'APR' : selectedReq.status === 'rejected' ? 'REJ' : 'PEN';
                const fullDocs = buildFixedDocs(docs, parentStatusBackend);
                setSelectedReq(prev => prev ? { ...prev, docs: fullDocs } : null);
            } catch (error) {
                console.error("Error al cargar detalle:", error);
            } finally {
                setLoadingDetail(false);
            }
        };

        fetchUserDocs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedReq?.id, isDrawerOpen]);

    // ----------------------------------------------------------------------
    // HANDLERS
    // ----------------------------------------------------------------------
    const showToast = (msg: string, type: "success" | "danger" | "info" | "warning" = "info") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleFilterChange = (filterId: string) => {
        setActiveFilter(filterId);
        setCurrentPage(1);
    };

    const handleOpenDrawer = (req: VerificationRequest) => {
        setSelectedReq(req);
        setIsDrawerOpen(true);
    };

    const handleDecision = async (decision: 'VALIDADO' | 'RECHAZADO') => {
        if (!selectedReq) return;

        setIsUpdating(true);
        const actionCode = decision === 'VALIDADO' ? 'APR' : 'REJ';
        const comments = decision === 'VALIDADO' ? 'Validado manualmente' : 'Documentación rechazada';
        const finalUrl = `ApproveRejectUserVerification/${selectedReq.id}?action=${actionCode}&comments=${encodeURIComponent(comments)}`;

        try {
            await apiClient.put(finalUrl, {});
            showToast(decision === 'VALIDADO' ? '✓ Identidad aprobada' : '✗ Solicitud rechazada', decision === 'VALIDADO' ? 'success' : 'danger');
            setIsDrawerOpen(false);
            fetchVerifications(); // Recargamos para ver el cambio real
        } catch (error) {
            console.error("Error al actualizar estado:", error);
            showToast("No se pudo procesar la solicitud.", "danger");
        } finally {
            setIsUpdating(false);
        }
    };

    const filteredRequests = requests.filter(req =>
        req.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex w-full h-screen bg-gray-50 text-gray-900 font-sans overflow-hidden">
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 md:px-[28px] py-4 md:py-[18px] border-b border-gray-200 bg-white sticky top-0 z-10 shrink-0">
                    <div>
                        <h1 className="text-[19px] font-bold flex items-center gap-[10px] m-0 text-gray-900">
                            {/* <svg width="22" height="22" fill="none" stroke="#4f6df5" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            </svg> */}
                            <Shield size={22} color="#4f6df5" strokeWidth={2} />
                            Validación de Identidad
                        </h1>
                        <p className="text-xs text-gray-500 mt-[2px] m-0">Revisión de documentos KYC de usuarios</p>
                    </div>
                </div>

                <ValidacionStatsStrip />

                <ValidacionFilterToolbar
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    activeFilter={activeFilter}
                    onFilterChange={handleFilterChange}
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-[24px] md:px-[28px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex flex-col">
                    {loading ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-20 text-gray-500">
                            <Loader2 className="h-10 w-10 animate-spin text-[#4f6df5] mb-3" />
                            <p>Cargando solicitudes...</p>
                        </div>
                    ) : filteredRequests.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-20 text-gray-400">
                            <ShieldCheck className="h-12 w-12 mb-3 text-gray-300" />
                            <p>No se encontraron solicitudes para este filtro.</p>
                        </div>
                    ) : (
                        <>
                            <ValidacionGrid
                                requests={filteredRequests}
                                onOpenDrawer={handleOpenDrawer}
                                onApprove={(id) => {
                                    const req = requests.find(r => r.id === id);
                                    if (req) {
                                        setSelectedReq(req);
                                        handleDecision('VALIDADO');
                                    }
                                }}
                            />

                            {pagination && pagination.totalPageCount > 1 && (
                                <div className="flex justify-center items-center gap-4 mt-8 pt-6 border-t border-gray-200">
                                    <button
                                        disabled={!pagination.hasPrevious}
                                        onClick={() => setCurrentPage(prev => prev - 1)}
                                        className="p-2 bg-white border border-gray-300 rounded-full hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>

                                    <span className="text-sm font-medium text-gray-600">
                                        Página {pagination.currentPage} de {pagination.totalPageCount}
                                    </span>

                                    <button
                                        disabled={!pagination.hasNext}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                        className="p-2 bg-white border border-gray-300 rounded-full hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <ValidacionDrawer
                isOpen={isDrawerOpen}
                selectedReq={selectedReq}
                loadingDetail={loadingDetail}
                isUpdating={isUpdating}
                onClose={() => setIsDrawerOpen(false)}
                onApprove={() => handleDecision('VALIDADO')}
                onReject={() => handleDecision('RECHAZADO')}
                onShowToast={showToast}
            />

            <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            {toast && (
                <div className="fixed bottom-6 right-6 z-[200] bg-white rounded-[10px] text-[13px] font-semibold py-3 px-5 shadow-lg border"
                    style={{
                        animation: "toastIn 0.3s ease forwards",
                        borderColor: toast.type === "success" ? "#10b981" : toast.type === "danger" ? "#ef4444" : toast.type === "warning" ? "#f59e0b" : "#e5e7eb",
                        color: toast.type === "success" ? "#059669" : toast.type === "danger" ? "#dc2626" : toast.type === "warning" ? "#d97706" : "#1f2937"
                    }}>
                    {toast.msg}
                </div>
            )}
        </div>
    );
};

export default ValidacionIdentidadPageNew;